import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import priceData from "../../Components/Price/PriceLogic";
import { useTranslation } from "react-i18next";
import { createPlan, getAllPlans } from "../../../../services/billing";
import PackageSummaryModal from "../../../Layout/DashboardLayout/Razorpay/PackageSummaryModal";
import RazorpayModal from "../../../Layout/DashboardLayout/Razorpay/RazorpayModal";
import { useRazorpay } from "../../../../context/RazorpayContext";
import PriceSectionCardSkeleton from "../Skeleton/PricingSkeleton";
import NoPlansAvailable from "./NoPlanAvailable";
import DescriptionList from "../../../../common/DescriptionList";
import Testimonial from "../Testimonials/Testimonial";
import { pricBg } from "../../../../../images";
import SEO from "../../../../SEO/Seo";
import { metaContent } from "../../../../SEO/metaContent";
import EnterprisePlanModal from "../../../Layout/DashboardLayout/BillingDashboard/modals/EnterprisePlanModal";
import { calculateStrikethrough } from "../../../../utils/pricingUtils";
import { is } from "date-fns/locale";

// --- Constants & Configs ---
const NUMBER_OF_SKELETON_CARDS = 1;
const AFTER_PAYMENT_SUCCESS_URL = "/dashboard";

// --- Custom Hook: Core Business Logic ---
// We encapsulate the entire data flow here to keep our Main Component strictly focused on UI rendering.
const usePricingThemeAndLogic = () => {
  const location = useLocation();
  const { invoiceModalData, razorpayModalData, handlePreviewPlan } =
    useRazorpay();

  // Combining all related scalar states into an object limits sporadic re-renders
  // and mimics the cleanliness of a useReducer architecture.
  const [state, setState] = useState({
    plans: null,
    isNoPlanAvailable: false,
    loading: true,
    showEnterpriseModal: false,
    selectedPlan: null,
    billingCycle: "monthly",
    traceBtnClick: { isLoading: false, btnClickPackageId: null },
  });

  // Derived logic
  const { heading, subheading } =
    priceData[location.pathname] || priceData["/"];
  const isPricingPage = location.pathname === "/pricing";

  // Data fetching effect
  useEffect(() => {
    let isMounted = true; // Guard against attempting UI updates after component completely unmounts

    const fetchPlans = async () => {
      try {
        const response = await getAllPlans();

        const activePlans = response?.data?.data || [];

        if (!activePlans.length) {
          if (isMounted)
            setState((prev) => ({
              ...prev,
              isNoPlanAvailable: true,
              loading: false,
            }));
          return;
        }

        // SORT IN ASCENDING ORDER
        activePlans.sort((a, b) => {
          const priceA = parseFloat(
            a?.prices?.monthly?.replace(/[₹,]/g, "") || 0,
          );

          const priceB = parseFloat(
            b?.prices?.monthly?.replace(/[₹,]/g, "") || 0,
          );

          return priceA - priceB;
        });

        const platinumExists = activePlans.some(
          (p) => p.name === "Platinum" || p.name === "Enterprise",
        );
        if (!platinumExists && (isPricingPage || location.pathname === "/")) {
          activePlans.push({
            id: "platinum-default",
            name: "Enterprise",
            subtag_line:
              "Get more access to new and powerful features to boost your productivity and creativity",
            price: "Enterprise",
            interval: "monthly",
            description:
              "For Big Brands Complete control & team collaboration Everything in Starter, plus\nCustom Integrations & Reports\nTeam Roles & Permissions\nDedicated account manager support\nQuarterly growth strategy review\nBest for large brands, distributors & agencies",
            isPopular: true,
          });
        }

        if (isMounted)
          setState((prev) => ({ ...prev, plans: activePlans, loading: false }));
      } catch (err) {
        console.error("Data Fetching Execution Error:", err);
        if (isMounted)
          setState((prev) => ({
            ...prev,
            isNoPlanAvailable: true,
            loading: false,
          }));
      }
    };

    fetchPlans();
    return () => {
      isMounted = false;
    };
  }, [location.pathname, isPricingPage]);

  // Page background manipulation effect
  useEffect(() => {
    const staticContainer = document.querySelector(".static-container");
    if (!staticContainer) return;

    // Cache original styles dynamically
    const styleProps = [
      "backgroundImage",
      "backgroundColor",
      "backgroundSize",
      "backgroundRepeat",
      "backgroundPosition",
      "backgroundAttachment",
    ];
    const originalStyles = styleProps.reduce(
      (acc, prop) => ({ ...acc, [prop]: staticContainer.style[prop] }),
      {},
    );

    // Utility closure for clean assignment
    const applyStyles = (obj) =>
      Object.keys(obj).forEach((key) => {
        staticContainer.style[key] = obj[key];
      });

    applyStyles({
      backgroundImage: `url(${pricBg})`,
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      transition: "background 0.5s ease-in-out",
    });

    return () => applyStyles(originalStyles);
  }, []);

  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (invoiceModalData?.isInvoiceDetailsModal) {
      setIsProcessing(false); //  stop loader
    }
  }, [invoiceModalData]);

  // Complex UI Interaction Callbacks
  // const handleSelectPlan = useCallback((e, plan) => {
  //   if (plan.isPopular) {
  //     setState(prev => ({ ...prev, selectedPlan: plan, showEnterpriseModal: true }));
  //   } else {
  //     // By dynamically updating our nested state object using a callback injection
  //     const updateTrace = (val) => setState(p => ({ ...p, traceBtnClick: val }));
  //     handlePreviewPlan(e, plan.id, 'plan', updateTrace, false, state.billingCycle);
  //   }
  // }, [handlePreviewPlan]);

  const changeLoadingState = () => {
    setState((prev) => ({
      ...prev,
      loading: false,
    }));
  };

  const handleSelectPlan = useCallback(
    async (e, plan) => {
      if (plan.isPopular) {
        setState((prev) => ({
          ...prev,
          selectedPlan: plan,
          showEnterpriseModal: true,
        }));
      } else {
        setIsProcessing(true); //
        const updateTrace = (val) =>
          setState((p) => ({ ...p, traceBtnClick: val }));

        const intervelPrice =
          state.billingCycle === "monthly"
            ? plan?.prices?.monthly
            : plan?.prices?.yearly;
        const isFree =
          intervelPrice === 0 ||
          intervelPrice === "₹0.00" ||
          intervelPrice === "0" ||
          intervelPrice === "0.00" ||
          intervelPrice === "Free";

        handlePreviewPlan(
          e,
          plan.id,
          "plan",
          updateTrace,
          false,
          state.billingCycle,
        );
      }
    },
    [handlePreviewPlan, state.billingCycle],
  );

  const createFreePlan = async (packageId, interval) => {
    try {
      const payload = {
        plan_id: packageId,
        interval: interval,
      };
      const apiRes = await createPlan(payload);
    } catch (err) {
      console.error(`Error in create free plan :`, err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleToggleCycle = useCallback((cycle) => {
    setState((prev) => ({ ...prev, billingCycle: cycle }));
  }, []);

  const closeModal = useCallback(() => {
    setState((prev) => ({ ...prev, showEnterpriseModal: false }));
  }, []);

  return {
    state,
    handlers: { handleSelectPlan, handleToggleCycle, closeModal },
    layout: { isPricingPage, heading, subheading },
    modals: { invoiceModalData, razorpayModalData },
    isProcessing,
  };
};

// --- Rendering Subcomponents ---
const BillingToggle = React.memo(
  ({ billingCycle, onToggle, annualSavePercent }) => (
    <div className="billing-toggle-wrapper mt-4">
      <div className="billing-toggle-inner-div">
        <button
          onClick={() => onToggle("monthly")}
          className={`toggle-btn monthly-btn ${billingCycle === "monthly" ? "active" : ""}`}
        >
          Monthly
        </button>
        <button
          onClick={() => onToggle("yearly")}
          className={`toggle-btn annual-btn ${billingCycle === "yearly" ? "active" : ""}`}
        >
          <span>Annual</span>
          {/* {annualSavePercent && (
            <span className="save-badge">{annualSavePercent}</span>
          )} */}
        </button>
      </div>
    </div>
  ),
);
//  Add this ABOVE component

const PricingCard = React.memo(
  ({ plan, billingCycle, isLoading, onSelectPlan }) => {
    const isEnterprise = plan.isPopular && plan.price === "Enterprise";

    const prices = useMemo(() => {
      const currentSave =
        billingCycle === "yearly"
          ? plan?.prices?.yearly_save || 0
          : plan?.prices?.monthly_save || 0;
      const currentPrice =
        billingCycle === "yearly"
          ? plan?.prices?.yearly
          : plan?.prices?.monthly;
      const strikePrice =
        billingCycle === "yearly"
          ? plan?.prices?.base_yearly
          : plan?.prices?.base_monthly;
      return {
        current: currentPrice,
        strikethrough: strikePrice,
        saveText:
          Number(String(currentSave).replace(/[^\d.]/g, "")) > 0
            ? `Save ${currentSave}`
            : "",
        discountText: plan.discount ? `(${plan.discount} off)` : "",
      };
    }, [billingCycle, plan]);

    const handleActionClick = useCallback(
      (e) => onSelectPlan(e, plan),
      [onSelectPlan, plan],
    );
    const getPriceValue = (price) => {
      if (!price) return 0;
      return parseFloat(price.toString().replace(/[^\d.]/g, ""));
    };

    const priceValue = getPriceValue(plan?.prices?.monthly);

    const strikeValue = (prices) => parseFloat(prices?.replace(/[^\d.]/g, ""));
    const isFreePlan =
      plan?.trial ||
      plan?.price === "Free" ||
      plan?.prices?.monthly === 0 ||
      plan?.prices?.monthly === "₹0.00";

    const iconClass = isFreePlan
      ? "bi bi-gift" // Free Plan
      : priceValue <= 999
        ? "bi bi-rocket-takeoff" // Starter
        : priceValue <= 1999
          ? "bi bi-graph-up-arrow" // Growth
          : "bi bi-building"; // Enterprise
    return (
      <div
        className={`modern-pricing-card 
          ${plan.isPopular ? "popular-card" : ""} 
          ${isFreePlan ? "free-card" : ""}`}
      >
        {plan?.most_popular && (
          <div className="popular-badge">★ Most Popular</div>
        )}
        {/*  HEADER SECTION: Side-by-side layout matching image_993c60.png */}
        <div className="d-flex align-items-center mb-4 text-start">
          {/* ICON LOGIC */}
          <div
            className="plan-icon-wrapper d-flex align-items-center justify-content-center me-3"
            style={{
              background: isFreePlan
                ? "linear-gradient(135deg, #16a34a, #22c55e)"
                : "#6366f1", // Adjust to match the purple in image_993c60.png
              width: "54px",
              height: "54px",
              borderRadius: "12px",
              flexShrink: 0,
            }}
          >
            {plan?.icon ? (
              <img
                src={plan.icon}
                alt="icon"
                className="plan-icon-img"
                style={{ width: "28px" }}
              />
            ) : (
              <i
                className={iconClass}
                style={{
                  fontSize: "24px",
                  color: "#fff",
                  background: "transparent",
                  // background:
                  // isFreePlan
                  //   ? "linear-gradient(135deg, #16a34a, #22c55e)"
                  //   : "linear-gradient(135deg, #6366f1, #4338ca)",
                }}
              ></i>
            )}
          </div>

          {/* TITLE & SUBTITLE GROUP */}
          <div className="d-flex flex-column">
            <h3
              className="text-white fw-bold mb-0"
              style={{ textTransform: "capitalize", fontSize: "20px" }}
            >
              {plan.name}
            </h3>
            {plan.subtag_line && (
              <p
                className="pricing-card-subtitle text-white-50 mb-0"
                style={{ fontSize: "14px", lineHeight: "1.3" }}
              >
                {plan.subtag_line}
              </p>
            )}
          </div>
        </div>

        <div
          className="modern-plan-price d-flex flex-column align-items-start mb-2"
          style={{ minHeight: "70px" }} // Increased to ensure the button starts at the same level
        >
          {isEnterprise ? (
            /*  Enterprise Logic using same alignment as other plans */
            <h2
              className="price-amount m-0 text-white fw-bold"
              style={{ fontSize: "2.2rem", lineHeight: "1.2" }}
            >
              {plan.price}
            </h2>
          ) : (
            <>
              {/* Current Price */}
              <h2
                className="price-amount m-0 text-white fw-bold"
                style={{ fontSize: "2.2rem", lineHeight: "1" }}
              >
                {plan?.trial ? (
                  <div className="d-flex flex-column align-items-start gap-1">
                    <span className="fs-3" style={{ color: "#10b981" }}>
                      {plan?.trial?.days} Days
                    </span>

                    <span className="fs-3">Free Trial</span>
                  </div>
                ) : (
                  <>
                    {prices.current}

                    {!plan?.isPopular && (
                      <span className="ms-1">
                        <span className="fs-4 fw-light text-white-50">/</span>

                        <span className="billing-cycle fs-6 fw-normal text-white-50">
                          {billingCycle === "yearly" ? "yr" : "mo"}
                        </span>
                      </span>
                    )}
                  </>
                )}
              </h2>

              {/* Strike Price + Save Text */}
              {!plan?.trial && (prices.strikethrough || prices.saveText) && (
                <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                  {strikeValue(prices.strikethrough) > 0 && (
                    <span className="text-decoration-line-through text-white-50 fs-6 fw-medium">
                      {prices.strikethrough}
                    </span>
                  )}

                  {(prices.saveText || prices.discountText) && (
                    <span
                      className="m-0"
                      style={{
                        color: "#10b981",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      {prices.saveText} {prices.discountText}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <div className="d-flex justify-content-center mb-2 w-100">
          <button
            className={`modern-buy-btn ${plan?.isPopular ? "popular-btn" : "standard-btn"}`}
            style={{
              background: isFreePlan
                ? "linear-gradient(135deg, #16a34a, #22c55e)"
                : "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: "50px",
              padding: "14px 24px",
              fontWeight: "600",
            }}
            onClick={handleActionClick}
            disabled={isLoading}
          >
            {plan.isPopular
              ? "Contact Now"
              : plan?.trial
                ? "Start Free Trial"
                : "Start My Plan"}
          </button>
        </div>
        <div className="modern-features-list text-start mt-3 flex-grow-1">
          <div className="text-start mb-3 mt-2">
            <DescriptionList
              description={plan.description}
              className={"text-white"}
              isFreePlan={isFreePlan}
            />
          </div>
        </div>
        {plan?.features?.length > 0 && (
          <div className="modern-features-list text-start mt-4">
            <ul className="list-unstyled">
              {plan.features.map((feature, idx) => (
                <li key={idx} className="d-flex align-items-start mb-3">
                  {/*  Checkmark Icon matching the purple style */}
                  <div
                    className="d-flex align-items-center justify-content-center me-3"
                    style={{
                      background: isFreePlan
                        ? "linear-gradient(135deg, #16a34a, #22c55e)"
                        : "linear-gradient(135deg, #6366f1, #4338ca)",
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      flexShrink: 0,
                      marginTop: "2px", // Aligns icon with the first line of text
                    }}
                  >
                    <i
                      className="bi bi-check-lg text-white"
                      style={{ fontSize: "12px" }}
                    ></i>
                  </div>

                  {/*  Feature Text */}
                  <span
                    className="text-white-50"
                    style={{ fontSize: "14px", lineHeight: "1.4" }}
                  >
                    {feature}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* <div className="plan-info-box">
          <i className="bi bi-shield-check"></i>
          <span>{plan?.info ? plan.info : "30-Day Money Back Guarantee"}</span>
        </div> */}
      </div>
    );
  },
);

// --- Main Page Component ---
// Extremely clean and declarative Root Component.
const Price = () => {
  const { t } = useTranslation();
  const { state, handlers, layout, modals, isProcessing } =
    usePricingThemeAndLogic();

  const sliderRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const showSlider = state.plans?.length > 3;

  const scrollSlider = (direction) => {
    if (!sliderRef.current) return;

    const scrollAmount = sliderRef.current.offsetWidth;

    sliderRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };
  const updateScrollButtons = () => {
    if (!sliderRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;

    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 5);
  };
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    updateScrollButtons();

    slider.addEventListener("scroll", updateScrollButtons);

    return () => slider.removeEventListener("scroll", updateScrollButtons);
  }, [state.plans]);

  const annualSavePercent = useMemo(() => {
    if (!state?.plans?.length) {
      return "";
    }

    const parsePrice = (price) =>
      Number(String(price || "").replace(/[₹,]/g, ""));

    const percentages = state.plans
      .map((plan) => {
        const base = parsePrice(plan?.prices?.base_yearly);
        const yearly = parsePrice(plan?.prices?.yearly);

        if (!base || !yearly || base <= yearly) {
          return 0;
        }

        return ((base - yearly) / base) * 100;
      })
      .filter(Boolean);

    if (!percentages.length) {
      return "";
    }

    return `Save ${Math.round(Math.max(...percentages))}%`;
  }, [state?.plans]);

  // Dynamic class assignment leveraging useMemo to ignore rendering changes for pure CSS string outputs
  const containerClassList = useMemo(
    () =>
      [
        state.plans?.length >= 3 ? "pricing-large-container" : "",
        !layout.isPricingPage && state.plans?.length === 1
          ? `pricing-container-one ${state.loading && "pricing-container-one-skeleton"}`
          : "",
        state.plans?.length < 3 &&
        (layout.isPricingPage || state.plans?.length !== 1)
          ? "pricing-container"
          : "",
      ]
        .filter(Boolean)
        .join(" "),
    [state.plans?.length, layout.isPricingPage],
  );

  if (state.isNoPlanAvailable) return <NoPlansAvailable />;

  return (
    <>
      {isProcessing && (
        <div className="global-loader-overlay">
          <div className="spinner-border text-light" role="status"></div>
        </div>
      )}

      {layout.isPricingPage && <SEO {...metaContent.pricing} />}

      {state.showEnterpriseModal && (
        <EnterprisePlanModal
          plan={state.selectedPlan}
          onClose={handlers.closeModal}
        />
      )}
      {modals.invoiceModalData?.isInvoiceDetailsModal && (
        <PackageSummaryModal
          billingCycle={state.billingCycle}
          packageType="plan"
          showCoupen={true}
        />
      )}
      {modals.razorpayModalData?.isRazorPayModal && (
        <RazorpayModal navigationUrl={AFTER_PAYMENT_SUCCESS_URL} />
      )}

      {/* Unified structural wrapper eliminates the duplicate layout blocks completely */}
      <div className={layout.isPricingPage ? "py-5" : "modern-price-container"}>
        <div
          className={`text-center ${layout.isPricingPage ? "text-start price-text-container" : ""}`}
        >
          <h2
            className={`hero-heading ${layout.isPricingPage ? "price-gradient-text text-white" : "text-white"}`}
          >
            {layout.heading}
          </h2>
          <p
            className={`hero-heading mt-1 ${layout.isPricingPage ? "text-white" : "price-gradient-text"}`}
          >
            {layout.subheading}
          </p>
          <p className="hero-sub-text mt-1 text-center">
            Invest in Insights That Guarantee Growth
          </p>
        </div>

        <BillingToggle
          billingCycle={state.billingCycle}
          onToggle={handlers.handleToggleCycle}
          annualSavePercent={annualSavePercent}
        />

        <div className={containerClassList || "pricing-container"}>
          {/* <div className="pricing-grid">
            {state.loading
              ? [...Array(NUMBER_OF_SKELETON_CARDS)].map((_, i) => (
                <PriceSectionCardSkeleton key={i} />
              ))
              : state.plans?.map((plan) => (
                <PricingCard
                  key={plan.id}
                  plan={plan}
                  billingCycle={state.billingCycle}
                  isLoading={state.traceBtnClick?.isLoading}
                  onSelectPlan={handlers.handleSelectPlan}
                />
              ))}
          </div> */}
          {showSlider ? (
            <div className="pricing-slider-wrapper">
              {/* Controls INSIDE wrapper */}
              <div className="slider-controls">
                <button
                  onClick={() => scrollSlider("left")}
                  disabled={!canScrollLeft}
                  className={!canScrollLeft ? "disabled-btn" : ""}
                >
                  ‹
                </button>

                <button
                  onClick={() => scrollSlider("right")}
                  disabled={!canScrollRight}
                  className={!canScrollRight ? "disabled-btn" : ""}
                >
                  ›
                </button>
              </div>

              <div className="pricing-slider" ref={sliderRef}>
                {state.loading
                  ? [...Array(NUMBER_OF_SKELETON_CARDS)].map((_, i) => (
                      <PriceSectionCardSkeleton key={i} />
                    ))
                  : state.plans?.map((plan) => (
                      <div className="pricing-slide" key={plan.id}>
                        <PricingCard
                          plan={plan}
                          billingCycle={state.billingCycle}
                          isLoading={state.traceBtnClick?.isLoading}
                          onSelectPlan={handlers.handleSelectPlan}
                        />
                      </div>
                    ))}
              </div>
            </div>
          ) : (
            <div className="pricing-grid">
              {state.loading
                ? [...Array(NUMBER_OF_SKELETON_CARDS)].map((_, i) => (
                    <PriceSectionCardSkeleton key={i} />
                  ))
                : state.plans?.map((plan) => (
                    <PricingCard
                      key={plan.id}
                      plan={plan}
                      billingCycle={state.billingCycle}
                      isLoading={state.traceBtnClick?.isLoading}
                      onSelectPlan={handlers.handleSelectPlan}
                    />
                  ))}
            </div>
          )}
        </div>

        {layout.isPricingPage && <Testimonial />}
      </div>
    </>
  );
};

export default Price;
